import actionTypes from 'constants/submissionFormConstants';
import checkoutActionTypes from 'constants/checkoutDataConstants';
import { setupAuthorizationToken, authorizedUrl } from 'lib/auth';
import { clearState } from 'lib/formStorage';
import { change } from 'redux-form';
import Cookies from 'js-cookie';
import { fetchBasicUserDataSuccess } from 'actions/currentUserActions';
import { goToFinalStep } from 'actions/finalWizardActions';
import Honeybadger from 'honeybadger-js';
import api from 'lib/evvntApi';
import hasIn from 'lodash/hasIn';
import * as appActions from 'actions/appActions';
import { trackPageView, trackEvent } from 'lib/eventTracking';

function getImagesBaseUrl(state) {
  const eventId = state.form.eventSubmission.values.id;
  const uuid = state.form.eventSubmission.values.uuid;
  return eventId ? `/events/${eventId}/images` : `/forms/${uuid}/images`;
}

export function preloadImages() {
  return (dispatch, getState, api) => {
    dispatch({ type: actionTypes.FETCH_IMAGE_REQUEST });

    api.get(getImagesBaseUrl(getState())).then(
      (response) => dispatch({ type: actionTypes.FETCH_IMAGE_SUCCESS, payload: response.data }),
      (error) => dispatch({ type: actionTypes.FETCH_IMAGE_FAILURE, payload: error })
    );
  };
}

export function uploadImage(image) {
  return (dispatch, getState, api) => {
    dispatch({ type: actionTypes.CREATE_IMAGE_REQUEST });

    const data = new FormData();
    data.append('file', image);

    api.post(getImagesBaseUrl(getState()), data).then(
      (response) => dispatch({ type: actionTypes.CREATE_IMAGE_SUCCESS, payload: response.data }),
      (error) => dispatch({ type: actionTypes.CREATE_IMAGE_FAILURE, payload: error.response.data.error })
    );
  };
}

export function removeImage(id) {
  return (dispatch, getState, api) => {
    dispatch({ type: actionTypes.REMOVE_IMAGE_REQUEST });

    const url = getImagesBaseUrl(getState()) + `/${id}`;
    api.delete(url).then(
      () => dispatch({ type: actionTypes.REMOVE_IMAGE_SUCCESS, payload: { id }}),
      (error) => dispatch({ type: actionTypes.REMOVE_IMAGE_FAILURE, payload: error })
    );
  };
}

const authorizedRedirectTo = (url) => () => {
  window.location = authorizedUrl(url);
};

const eventCreatedSuccessfuly = (response, history, skipRedirect = false) => dispatch => {
  clearState(); // clear form data
  if (response.current_user) {
    setupAuthorizationToken(response.current_user.token);
    dispatch(fetchBasicUserDataSuccess(response.current_user));
  }
  if (response.redirect_to) {
    dispatch(authorizedRedirectTo(response.redirect_to));
  } else if (response.uuid) {
    Cookies.set('evvntDraftUUID', response.uuid);
    dispatch(change('eventSubmission', 'id', response.event_id));
    dispatch({ type: actionTypes.CREATE_EVENT_SUCCESS });
  } else {
    dispatch({ type: actionTypes.CREATE_EVENT_SUCCESS });
    dispatch({ type: checkoutActionTypes.SETUP_CHECKOUT, payload: response.checkout_data });

    if (skipRedirect) {
      dispatch(goToFinalStep('new_account'));
    } else {
      history.push('/checkout');
    }
  }
};

const eventCreationFailed = error => dispatch => {
  dispatch({ type: actionTypes.CREATE_EVENT_FAILURE });
  if (error.response && error.response.status === 500) {
    dispatch(appActions.showAppFatalError('Something unexpected has happened.'));
  } else if(error.response && error.response.status === 422) {
    dispatch(appActions.showAppFatalError(error.response.data.error.details));
  }
};

const submitFormRequest = (data, config, history) => (dispatch, getState, api) => {
  dispatch({ type: actionTypes.CREATE_EVENT_REQUEST });
  if (config.get('partner_api_key')) {
    data['partner_api_key'] = config.get('partner_api_key');
  }
  if (config.get('publisher_id')) {
    data['publisher_id'] = config.get('publisher_id');
  }
  if (config.get('premium_edit')) {
    data['premium_edit'] = config.get('premium_edit');
  }

  const snapSellPackageId = getState().checkoutData.get('snap_sell_package_id', null);
  if (snapSellPackageId) {
    data['snap_sell_package_id'] = snapSellPackageId;
  }

  data['subdomain'] = window.location.host.split('.')[0];
  data['draft'] = false;

  const params = {
    event_data: data
  };
  const isUpdating = !!data.id;
  const skipRedirect = getState().finalWizard.get('currentStep') === 'account' && !snapSellPackageId;

  if (isUpdating) {
    api.put(`/events/${data.id}`, params).then(
      (response) => dispatch(eventCreatedSuccessfuly(response.data, history, skipRedirect)),
      (error) => dispatch(eventCreationFailed(error)),
    );
  } else {
    api.post('/events', params).then(
      (response) => dispatch(eventCreatedSuccessfuly(response.data, history, skipRedirect)),
      (error) => dispatch(eventCreationFailed(error)),
    );
  }
};

export function submitForm(stepId, history, forceSubmit = false) {
  return (dispatch, getState) => {
    const state = getState();
    const steps = state.submissionForm.get('steps');
    if(isLastStep(stepId, steps)) {
      const data = state.form.eventSubmission.values;
      const currentUser = state.currentUser;
      const config = state.config;
      if (currentUser.get('isLoggedIn') || (forceSubmit && (data.email && data.name))) {
        dispatch(submitFormRequest(data, config, history));
      } else {
        history.push('/submit');
      }
    } else {
      dispatch(goToNextStep(stepId, history));
    }
  };
}

export function saveDraft(currentStep) {
  return (dispatch, getState, api) => {
    const data = getState().form.eventSubmission.values;
    dispatch({ type: actionTypes.CREATE_DRAFT_REQUEST, payload: data });
    const isUpdating = !!data.id;
    data['draft'] = true;
    data['last_finished_step'] = currentStep;

    if (isUpdating) {
      api.put(`/events/${data.id}`, { event_data: data }).then(
        (response) => dispatch(eventCreatedSuccessfuly(response.data, history)),
        (error) => dispatch(eventCreationFailed(error)),
      );
    } else {
      api.post('/events', { event_data: data }).then(
        (response) => dispatch(eventCreatedSuccessfuly(response.data, history)),
        (error) => dispatch(eventCreationFailed(error)),
      );
    }
  };
}

export function prepareDiscoveryPreview() {
  return (dispatch, getState, api) => {
    let data = getState().form.eventSubmission.values;
    data.use_default_zone = true;

    api.post('/events/preview', { event_data: data }).then(
      (response) => dispatch({type: actionTypes.SUBMISSION_FORM_PREPARE_PREVIEW, payload: response.data }),
      (error) => dispatch(eventCreationFailed(error))
    );
  };
}

export function goToNextStep(currentStepId, history) {
  return (dispatch, getState) => {
    const steps = getState().submissionForm.get('steps');
    dispatch(goToStep(currentStepId, 1, steps, history));
    dispatch({ type: actionTypes.SUBMISSION_FORM_STEP_COMPLETED, payload: { step: currentStepId } });
  };
}

export function goToPreviousStep(currentStepId, history) {
  return (dispatch, getState) => {
    const steps = getState().submissionForm.get('steps');
    if (!hasFormErrors(steps)){
      dispatch(goToStep(currentStepId, -1, steps, history));
    }
  };
}

export function hasFormErrors(steps){
  let anyErrors = false;

  steps.valueSeq().forEach(stepMap => {
    if (!anyErrors){
      anyErrors = stepMap.get('errors').size > 0;
    }
  });
  
  return anyErrors;
}

function goToStep(currentStepId, modifier, steps, history) {
  return () => {
    const keys = steps.keySeq().toArray();
    const nextStep = keys[keys.indexOf(currentStepId) + modifier];
    const stepPath = steps.getIn([nextStep, 'path']);
    trackPageView('Submission Form Step: ' + nextStep );
    history.push({ pathname: stepPath });
  };
}

function isLastStep(stepId, steps) {
  const keys = steps.keySeq().toArray();
  return keys.indexOf(stepId) === keys.length - 1;
}

export function setFormErrors(stepId, errors) {
  return (dispatch) => dispatch({
    type: actionTypes.SUBMISSION_FORM_VALIDATION_SUCCESS,
    payload: { stepId, errors }
  });
}

export const stepValidationFactory = (stepId) => (values, dispatch) => {
  dispatch({ type: actionTypes.SUBMISSION_FORM_VALIDATION_REQUEST });
  const params = {
    event_data: values,
    just_validate: true,
    step: stepId,
  };

  return validationRequest(dispatch, params, stepId);
};

const validationRequest = (dispatch, params, stepId) => {
  return new Promise((resolve, reject) => {
    api.post('/events', params).then(
      () => {
        dispatch(setFormErrors(stepId, {}));
        resolve();
      },
      request => {
        const status = request.response.status;
        let errors = {};

        if (status === 500) {
          dispatch(setFormErrors(stepId, errors));
          resolve();

          Honeybadger.notify(request, {
            name: 'Step validation failed',
            parameters: params,
            context: {
              stepId: stepId
            }
          });
        } else if (status === 401) {
          dispatch(appActions.showAppFatalError('You are not authorized to perform this action. Please contact support.'));
          resolve();

        } else {
          errors = request.response.data.error.details;
          dispatch(setFormErrors(stepId, errors));
          reject(errors);
        }
      }
    );
  });
};

export const triggerFormValidation = () => (dispatch, getState) => {
  dispatch({ type: actionTypes.SUBMISSION_FORM_VALIDATION_REQUEST });
  const values = getState().form.eventSubmission.values;
  const stepId = values.quick_form ? 'quick' : 'basics';
  const params = {
    event_data: values,
    just_validate: true,
    step: stepId,
  };
  return validationRequest(dispatch, params, stepId);
};


export const clearAddressErrors = () => (dispatch, getState) => {
  const currentForm = getState().form.eventSubmission.values.quick_form ? 'quick' : 'basics';

  dispatch(setFormErrors(currentForm, {
    ...getState().submissionForm.getIn(['steps', currentForm, 'errors']).toJS(),
    post_code: null,
    state_name: null,
    venue: {},
  }));
};

export const clearPreviewEvent = () => (dispatch, _getState) => {
  dispatch({ type: actionTypes.SUBMISSION_FORM_CLEAR_PREVIEW });
};

export const preloadStepsState = (history) => (dispatch, getState) => {
  const form = getState().form.eventSubmission;
  const premiumEdit = getState().config.get('premium_edit');
  const draft_available = getState().config.get('draft_available');

  if (hasIn(form, 'values.last_finished_step')) {
    const lastStep = form.values.last_finished_step;
    const steps = getState().submissionForm.get('steps');
    const stepsKeys = steps.keySeq();
    const lastStepIndex = stepsKeys.indexOf(lastStep);
    for(let x = 0; x < lastStepIndex; x++) {
      dispatch({ type: actionTypes.SUBMISSION_FORM_STEP_COMPLETED, payload: { step: stepsKeys.get(x) } });
    }

    if (premiumEdit || draft_available){
      dispatch(goToStep(form.values.quick_form ? 'quick' : 'basics', 0, steps, history));
    } else if( !history.location.pathname || history.location.pathname === '/' ||
      history.location.pathname === '/basics'|| history.location.pathname === '/quick') {
      dispatch(goToStep(lastStep, 0, steps, history));
    }
  }
};

export const switchForm = (newForm = null) => (dispatch, getState) => {
  const currentForm = getState().form.eventSubmission.values.quick_form ? 'quick' : 'full';

  if (!newForm) {
    newForm = currentForm === 'quick' ? 'full' : 'quick';
  }

  if (newForm !== currentForm){
    trackEvent('Switch Submission Form', { from: currentForm, to: newForm});
    trackPageView(newForm === 'quick' ? 'Quick Submission Form' : 'Full Submission Form' );
  }

  dispatch(change('eventSubmission', 'quick_form', newForm === 'quick'));
  dispatch({ type: actionTypes.SUBMISSION_FORM_SWITCH, payload: { form: newForm } });
};



// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/actions/submissionFormActions.js